// @mui material components
import Card from "@mui/material/Card";
import React, { useMemo, useEffect, useState, useRef } from "react";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkIcon from "@mui/icons-material/Link";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useParams } from "react-router-dom";
import useStore from "store/hook";
import DeleteProject from "components/SoftDialog/DeleteProject";
import CustomTable from "examples/Tables/Table";
import UserRow, { columns } from "store/action/ProjectOverview";
import DeficiencyRow, { Deficiencycolumns } from "store/action/Deficiency";
import headerBg from "assets/images/curved-images/Header.png";
import { Link } from "react-router-dom";
import api from "store/api/api";
import { useNavigate } from "react-router-dom";
import MarkComplete from "components/SoftDialog/MarkComplete";
import { ReactComponent as TickIcon } from "assets/images/small-logos/logo-tick.svg";
import { ReactComponent as EmptyFolderIcon } from "assets/images/small-logos/icon-emptyFolder.svg";
import SoftAvatar from "components/SoftAvatar";
import truncateString from "../../services/truncatestring";
import DeleteInspection from "components/SoftDialog/DeleteInspection";
import SoftPagination from "components/SoftPagination";
import CircularLoader from "components/SoftDialog/Loader";
import formatPhoneNumber from "services/formatPhoneNo";
import { FaPlus } from "react-icons/fa";
import { MdOutlineDownload } from "react-icons/md";
import AddDeficiency from "components/SoftDialog/AddDeficiency";
import SelectInspection from "components/SoftDialog/SelectInspection";
import DeleteDeficiencylog from "components/SoftDialog/DeleteDeficiencylog";
import convertMillisecondsToOnlyDate from "services/converMillisecondstoDate";
import brand from "assets/images/logos/image.png";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";
import { Container, Table, TableCell, TableRow } from "@mui/material";
import getFormattedDate from "services/simpleDateformat";

function ProjectOverview() {
  const navigate = useNavigate();
  const { setDialogue, store } = useStore();
  const { id } = useParams();
  const [projectData, setProjectData] = useState(null);
  const [inspectionData, setInspectionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const projectId = store?.app?.dialogs?.markAsComplete?.projectId || "";
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = useState(null);
  const [deficiencylogPagination, setDeficiencylogPagination] = useState(null);
  const [showInspections, setShowInspections] = useState(true);
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const [deficiecncylogData, setDeficiencylogData] = useState(null);
  const [downloadDeficiencylogData, setDownloadDeficiencylogData] =
    useState(null);
  const [logpdfLoading, setlogpdfLoading] = useState(false);
  const [deficiencylogLoading, setDeficiencylogLoading] = useState(false);
  let lastFailedRequest = false;
  const { inspectionId } = store?.app?.dialogs?.deleteInspection || {};
  const { success } = store?.app?.dialogs?.isSuccess || {};

  useEffect(() => {
    const loginData = JSON.parse(localStorage.getItem("loginData"));
    if (!loginData) {
      navigate("/authentication/sign-in");
    } else {
      setIsLoading(true);
      fetchProjectDetail();
      fetchInspectionList();
      fetchDeficiencyList();
      fetchAllDeficiencyList();
      setIsLoading(false);
    }
  }, [navigate, projectId, page, inspectionId, success]);

  const fetchProjectDetail = async () => {
    try {
      setIsLoading(true);
      const response = await api.getProjectDetail({
        project_id: id,
        is_deleted_needed: true,
      });
      setProjectData(response?.data?.project);
    } catch (error) {
      lastFailedRequest = true;
      toast.error("Oops! Slow network or server issue. Please try again.");
      console.error("Error fetching project detail:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInspectionList = async () => {
    try {
      setIsLoading(true);
      const response = await api.getInspectionListByProject({
        project_id: id,
        page: page,
        limit: 50,
      });

      setInspectionData(response?.data?.inspections);
      setPagination(response?.data?.pagination);
    } catch (error) {
      lastFailedRequest = true;
      console.error("Error fetching project Inspections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeficiencyList = async () => {
    try {
      setDeficiencylogLoading(true);
      const response = await api.getDeficiecnylogList({
        projectId: id,
        page: page,
        limit: 50,
      });

      setDeficiencylogData(response?.data?.deficiecncylog);
      setDeficiencylogPagination(response?.data?.pagination);
    } catch (error) {
      lastFailedRequest = true;
      console.error("Error fetching Deficiency log:", error);
    } finally {
      setDeficiencylogLoading(false);
    }
  };

  const fetchAllDeficiencyList = async () => {
    try {
      setIsLoading(true);
      const response = await api.getAllDeficiecnylogList({
        id: id,
        type: "project",
      });
      if (response?.status == 1) {
        setDownloadDeficiencylogData(response?.data);
      }
    } catch (error) {
      console.error("Error fetching Deficiency log:", error);
    } finally {
      setIsLoading(false);
    }
  };

  window.addEventListener("online", () => {
    if (lastFailedRequest) {
      fetchProjectDetail();
      fetchInspectionList();
      fetchDeficiencyList();
      lastFailedRequest = false;
    }
  });

  const rows = useMemo(
    () =>
      inspectionData?.length
        ? inspectionData.map((inspection) => UserRow(inspection, setDialogue))
        : [],
    [inspectionData, setDialogue]
  );

  const deficiencyRows = useMemo(
    () =>
      deficiecncylogData?.length
        ? deficiecncylogData.map((deficiency) =>
            DeficiencyRow(deficiency, setDialogue, id)
          )
        : [],
    [deficiecncylogData, setDialogue]
  );

  const handleAddInspection = () => {
    navigate(`/add-inspection/${id}`);
  };

  const deficiencypdfRef = useRef(null);

  const generateDeficiencyPDF = async () => {
    if (!deficiencypdfRef.current) return;

    setlogpdfLoading(true);
    const element = deficiencypdfRef.current;

    const options = {
      margin: [70, 5, 15, 5],
      filename: `${projectData?.name}_DeficiencyLog.pdf`,
      image: { type: "jpeg", quality: 0.7 },
      html2canvas: { scale: window.devicePixelRatio * 3 || 3, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    const pdf = await html2pdf().from(element).set(options).toPdf().get("pdf");

    const totalPages = pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const leftX = pageWidth / 4;
    const rightX = (3 * pageWidth) / 4;
    const topY = pageHeight - 215; // Move everything up
    const lineOffset = 3; // Adjust line height
    const textOffset = 6; // Adjust text positioning
    const signatureHeight = 20; // Reduce signature image height
    const signatureWidth = 40; // Reduce signature image width

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);

      // pagno
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 130, pageHeight - 10, {
        align: "right",
      });

      // left side footer
      const footerText1 = [
        "For complete deficiency details",
        "see report listed under deficiency PIF # column",
      ];

      footerText1.forEach((line, index) => {
        pdf.text(line, pageWidth / 6 - 38, pageHeight - 15 + index * 5, {
          align: "left",
        });
      });

      pdf.addImage(
        brand,
        rightX - 90,
        topY + textOffset + 2,
        signatureWidth,
        signatureHeight
      );

      pdf.text(
        "Helping Ensure a Safer Built Environment",
        rightX - 70,
        topY + textOffset + 25,
        {
          align: "center",
        }
      );

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);

      pdf.text("Deficiency Log", rightX - 70, topY + textOffset + 35, {
        align: "center",
      });

      pdf.text(projectData?.name, rightX - 70, topY + textOffset + 48, {
        align: "center",
      });

      pdf.text(
        projectData?.project_number,
        rightX - 70,
        topY + textOffset + 53,
        {
          align: "center",
        }
      );

      pdf.text(
        `Revision Date: ${getFormattedDate(Math.floor(Date.now() / 1000))}`,
        rightX - 184,
        topY + textOffset + 43,
        {
          align: "center",
        }
      );

      if (i > 0) {
        // box line1
        pdf.line(
          pageWidth / 2 - 136.9,
          pageHeight - 155 + lineOffset,
          pageWidth - 12,
          pageHeight - 155 + lineOffset
        );

        // box line 2
        pdf.line(
          pageWidth / 2 - 136.9,
          pageHeight - 143 + lineOffset,
          pageWidth - 12,
          pageHeight - 143 + lineOffset
        );

        // first
        pdf.line(
          pageWidth / 2 - 93.5,
          pageHeight - 243 + lineOffset,
          pageWidth - 11.5,
          pageHeight - 243 + lineOffset
        );

        // second
        pdf.line(
          pageWidth / 6 - 26.3,
          57.9,
          pageWidth / 6 - 26.3,
          pageHeight - 140
        );

        // first
        pdf.line(
          pageWidth / 6 - 38,
          57.9,
          pageWidth / 6 - 38,
          pageHeight - 140
        );

        // last line
        pdf.line(pageWidth - 12, 57.9, pageWidth - 12, pageHeight - 140);

        // last one
        pdf.line(pageWidth - 33, 57.9, pageWidth - 33, pageHeight - 140);

        // last second
        pdf.line(pageWidth - 56.8, 57.9, pageWidth - 56.8, pageHeight - 140);

        // third
        pdf.line(
          pageWidth / 5 - 12.1,
          57.9,
          pageWidth / 5 - 12.1,
          pageHeight - 140
        );

        // fourth
        pdf.line(
          pageWidth / 4 - 5.8,
          57.9,
          pageWidth / 4 - 5.8,
          pageHeight - 140
        );

        pdf.setFontSize(10);

        pdf.text(`Type Of Inspection`, rightX - 70, topY + textOffset + 62, {
          align: "center",
        });

        pdf.text(`List Of Deficiencies`, rightX - 70, topY + textOffset + 67, {
          align: "center",
        });

        pdf.text(`Deficiecy`, rightX - 188, topY + textOffset + 62, {
          align: "center",
        });

        pdf.text(`Date`, rightX - 188, topY + textOffset + 67, {
          align: "center",
        });

        pdf.text(`Def.`, rightX - 165.5, topY + textOffset + 62, {
          align: "center",
        });

        pdf.text(`PIF #`, rightX - 165.5, topY + textOffset + 67, {
          align: "center",
        });

        pdf.text(`Correction`, rightX + 30, topY + textOffset + 62, {
          align: "center",
        });

        pdf.text(`Date`, rightX + 30, topY + textOffset + 67, {
          align: "center",
        });

        pdf.text(`Corrected`, rightX + 51.2, topY + textOffset + 62, {
          align: "center",
        });

        pdf.text(`PIF  #`, rightX + 51.2, topY + textOffset + 67, {
          align: "center",
        });
      }
    }

    pdf.save(options.filename);
    setlogpdfLoading(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <DeleteProject />
      <DeleteDeficiencylog />
      <AddDeficiency />
      <DeleteInspection type="projectoverview" />
      <MarkComplete
        status={
          projectData?.status === "completed" ? "Completed" : "Incomplete"
        }
      />
      <SoftBox py={1}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox
              sx={{
                backgroundColor: "primary.main",
                backgroundImage: `url(${headerBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={{ xs: 2, md: 3 }}
              py={2.5}
            >
              <SoftTypography color="white" variant="h6" fontSize="20px">
                <Link to="/projects" style={{ color: "white" }}>
                  <SoftTypography
                    component="span"
                    fontWeight="light"
                    variant="h6"
                    fontSize="20px"
                  >
                    Projects &gt;{" "}
                  </SoftTypography>
                </Link>
                Project Overview
              </SoftTypography>
            </SoftBox>

            {isLoading ? (
              <SoftBox
                display="flex"
                height="calc(100vh - 180px)"
                justifyContent="center"
                alignItems="center"
                p={3}
              >
                <SoftTypography variant="subtitle1" color="textSecondary">
                  <CircularLoader type="large" />
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox
                sx={{ height: "calc(100vh - 190px)", overflowY: "auto" }}
              >
                <SoftBox
                  px={{ xs: 2, md: 3 }}
                  py={3}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                  }}
                  justifyContent="space-between"
                >
                  <SoftBox
                    sx={{
                      width: {
                        xs: "100%",
                        lg: "63%",
                      },
                    }}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    pr={5}
                  >
                    <SoftTypography
                      color="info"
                      fontWeight="bold"
                      fontSize="36px"
                      textTransform="capitalize"
                    >
                      {projectData?.name} - {projectData?.project_number}
                    </SoftTypography>

                    <SoftBox
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                      gap={12}
                    >
                      <SoftBox>
                        <SoftTypography
                          color="lightBlack"
                          fontSize="20px"
                          fontWeight="bold"
                        >
                          Project Manager
                        </SoftTypography>
                        <SoftTypography
                          color="lightBlack"
                          fontSize="16px"
                          display="flex"
                          gap={2}
                          textTransform="capitalize"
                          sx={{}}
                        >
                          {projectData?.manager?.map((manager, index) => (
                            <Link
                              to={`/inspector-overview/${manager?._id}`}
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              <SoftBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "start",
                                  cursor: "pointer",
                                  transition: "background-color 0.3s",
                                }}
                              >
                                <SoftAvatar
                                  src={manager.profile_picture || ""}
                                  alt={truncateString(manager.full_name, 5)}
                                  size="sm"
                                  variant="rounded"
                                />
                                <SoftTypography
                                  className="inspector-name"
                                  variant="text"
                                  fontSize="14px"
                                  color="#212121"
                                  textTransform="capitalize"
                                >
                                  {truncateString(manager.full_name, 25)}
                                </SoftTypography>
                              </SoftBox>
                            </Link>
                          ))}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox>
                        <SoftTypography
                          color="lightBlack"
                          fontSize="20px"
                          fontWeight="bold"
                        >
                          Location
                        </SoftTypography>
                        <SoftTypography
                          color="lightBlack"
                          fontSize="14px"
                          textTransform="capitalize"
                        >
                          {projectData?.location}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>

                  {!projectData?.is_deleted && (
                    <SoftBox
                      display="flex"
                      py={1.5}
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="start"
                      gap={1}
                      sx={{
                        width: "37%",
                        width: {
                          xs: "100%",
                          lg: "37%",
                        },
                      }}
                    >
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                        pb={2}
                        sx={{
                          width: "100%",
                        }}
                      >
                        {loginData?.account_type !== "inspector" && (
                          <SoftButton
                            variant="gradient"
                            color="primary"
                            sx={{
                              padding: "0px",
                              width: "100%",
                              fontSize: "14px",
                              borderRadius: "8px",
                            }}
                            onClick={() => navigate(`/projects/edit/${id}`)}
                          >
                            Edit Project
                          </SoftButton>
                        )}

                        {loginData?.account_type !== "inspector" && (
                          <SoftButton
                            onClick={() => {
                              setDialogue({
                                type: "markAsComplete",
                                isOpen: true,
                                projectId: id,
                                isCompleted: true,
                              });
                            }}
                            variant="gradient"
                            color="info"
                            sx={{
                              padding: "0px",
                              width: "100%",
                              fontSize: "14px",
                              position: "relative",
                              borderRadius: "8px",
                            }}
                          >
                            <TickIcon
                              style={{
                                position: "absolute",
                                left: "5px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                height: "16px",
                              }}
                            />
                            {projectData?.status === "completed"
                              ? "Re-Open Project"
                              : "Mark as complete"}
                          </SoftButton>
                        )}
                      </SoftBox>

                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                        sx={{
                          width: "100%",
                        }}
                      >
                        {loginData?.account_type !== "inspector" && (
                          <SoftButton
                            onClick={() =>
                              setDialogue({
                                type: "deleteProject",
                                isOpen: true,
                                projectId: id,
                                is_deleted: true,
                              })
                            }
                            variant="gradient"
                            color="error"
                            sx={{
                              padding: "0px",
                              width: "100%",
                              fontSize: "14px",
                              borderRadius: "8px",
                              backgroundColor: "#FF4646",
                            }}
                          >
                            Remove Project
                          </SoftButton>
                        )}

                        {/* <Link to={`/add-inspection/${id}`}> */}
                        {loginData?.account_type !== "inspector" && (
                          <SoftButton
                            variant="gradient"
                            color="primary"
                            sx={{
                              padding: "0px",
                              width: "100%",
                              fontSize: "14px",
                              borderRadius: "8px",
                              backgroundColor: "#FF4646",
                            }}
                            onClick={() => handleAddInspection()}
                          >
                            Start New Inspection
                          </SoftButton>
                        )}
                        {/* </Link> */}
                      </SoftBox>

                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="end"
                        gap={2}
                        sx={{
                          width: "100%",
                        }}
                      >
                        {loginData?.account_type == "inspector" && (
                          <SoftButton
                            variant="gradient"
                            color="primary"
                            sx={{
                              padding: "0px",
                              width: "200px",
                              fontSize: "14px",
                              borderRadius: "8px",
                              backgroundColor: "#FF4646",
                            }}
                            onClick={() => handleAddInspection()}
                          >
                            Start New Inspection
                          </SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox
                  width="100%"
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                  gap={1}
                  px={3}
                  pb={4}
                >
                  {projectData?.internal_notes && (
                    <SoftBox
                      border="0.5px solid #000000"
                      borderRadius="6px"
                      padding="10px"
                      maxHeight="130px"
                      height="100%"
                      sx={{
                        width: "100%",
                        overflowY: "scroll",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                      }}
                    >
                      <SoftTypography
                        color="lightBlack"
                        fontSize="16px"
                        fontWeight="bold"
                      >
                        Internal Notes
                      </SoftTypography>
                      <SoftTypography
                        color="lightBlack"
                        fontSize="10px"
                        fontWeight="medium"
                        paddingTop="5px"
                      >
                        {projectData?.internal_notes}
                      </SoftTypography>
                    </SoftBox>
                  )}

                  {projectData?.links?.length > 0 && (
                    <SoftBox
                      sx={{
                        width: "100%",
                      }}
                    >
                      <SoftTypography
                        color="lightBlack"
                        fontSize="20px"
                        fontWeight="bold"
                      >
                        Project Links
                      </SoftTypography>
                      {projectData?.links?.map((link, index) => (
                        <SoftTypography
                          key={index}
                          color="primary"
                          fontSize="14px"
                          display="block"
                          flexWrap="wrap"
                          alignItems="start"
                          justifyContent="start"
                          sx={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            width: "100%",
                          }}
                        >
                          <Link
                            to={link}
                            target="_blank"
                            sx={{ textDecoration: "none", color: "inherit" }}
                          >
                            <SoftBox
                              display="flex"
                              alignItems="center"
                              justifyContent="start"
                              sx={{ color: "primary.main", width: "fit-content", }}
                            >
                              <LinkIcon
                                sx={{
                                  color: "primary.main",
                                  marginRight: "8px",
                                  width: 16,
                                  height: 16,
                                  transform: "rotate(-40deg)",
                                  flexShrink: 0,
                                }}
                              />
                              {link}
                            </SoftBox>
                          </Link>
                        </SoftTypography>
                      ))}
                    </SoftBox>
                  )}

                  {projectData?.site_contacts?.length > 0 && (
                    <SoftBox
                      sx={{
                        width: "100%",
                      }}
                    >
                      <SoftTypography
                        color="#212121"
                        fontSize="20px"
                        fontWeight="bold"
                      >
                        Project Contacts
                      </SoftTypography>
                      {projectData?.site_contacts?.map((contact, index) => (
                        <SoftBox
                          key={index}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            backgroundColor: "#F1F4F8",
                            borderRadius: "8px",
                            padding: "5px 8px",
                            marginBottom: "4px",
                            overflowX: "auto",
                            overflowY: "hidden",
                            whiteSpace: "nowrap",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                        >
                          <SoftBox
                            display="flex"
                            flexDirection="column"
                            color="primary"
                          >
                            <SoftBox
                              display="flex"
                              alignItems="center"
                              justifyContent="start"
                              gap="8px"
                            >
                              <SoftTypography
                                fontSize="14px"
                                color="primary"
                                textTransform="uppercase"
                                fontWeight="medium"
                              >
                                {contact?.name}
                              </SoftTypography>

                              <SoftTypography
                                fontSize="12px"
                                color="primary"
                                textTransform="capitalize"
                                fontWeight="medium"
                              >
                                {contact?.companyName}
                              </SoftTypography>
                            </SoftBox>

                            <SoftBox display="flex" gap="8px">
                              {contact?.email && (
                                <a
                                  href={`mailto:${contact?.email}`}
                                  sx={{
                                    textDecoration: "none",
                                    color: "inherit",
                                  }}
                                >
                                  <SoftBox
                                    display="flex"
                                    gap="8px"
                                    color="primary"
                                  >
                                    <EmailIcon color="primary" />
                                    <SoftTypography
                                      fontSize="14px"
                                      color="primary"
                                    >
                                      {contact?.email}
                                    </SoftTypography>
                                  </SoftBox>
                                </a>
                              )}

                              {contact?.phoneNo && (
                                <SoftBox
                                  display="flex"
                                  gap="8px"
                                  color="primary"
                                >
                                  <PhoneIcon color="primary" />
                                  <SoftTypography
                                    fontSize="14px"
                                    color="primary"
                                  >
                                    {contact?.country_code}{" "}
                                    {formatPhoneNumber(contact?.phoneNo)}
                                  </SoftTypography>
                                </SoftBox>
                              )}
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox
                  sx={{
                    paddingTop: "28px",
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({
                          borders: { borderWidth, borderColor },
                        }) => `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <>
                    <SoftBox px={2}>
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={{
                          overflowX: "scroll",
                          scrollbarWidth: "none",
                          "&::-webkit-scrollbar": { display: "none" },
                        }}
                      >
                        {/* left side */}
                        <SoftBox display="flex" pl={1} gap={3}>
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            py={1}
                            px={5.5}
                            sx={{
                              border: showInspections
                                ? "2px solid #02255B"
                                : "2px solid #737373",
                              backgroundColor: showInspections
                                ? "#02255B"
                                : "#F1F4F8",
                              borderTopLeftRadius: "15px",
                              borderTopRightRadius: "15px",
                              cursor: "pointer",
                              borderBottom: "none",
                            }}
                            onClick={() => {
                              setShowInspections(true);
                              setPage(1);
                            }}
                          >
                            <SoftTypography
                              color={showInspections ? "white" : "text"}
                              fontWeight="medium"
                              fontSize="18px"
                            >
                              Inspections
                            </SoftTypography>
                          </SoftBox>

                          <SoftBox
                            py={1}
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              borderTopLeftRadius: "15px",
                              borderTopRightRadius: "15px",
                              cursor: "pointer",
                              border: !showInspections
                                ? "2px solid #02255B"
                                : "2px solid #737373",
                              backgroundColor: !showInspections
                                ? "#02255B"
                                : "#F1F4F8",
                              borderTopLeftRadius: "15px",
                              borderTopRightRadius: "15px",
                              cursor: "pointer",
                              borderBottom: "none",
                            }}
                            onClick={() => {
                              setShowInspections(false);
                              setPage(1);
                            }}
                          >
                            <SoftTypography
                              color={!showInspections ? "white" : "text"}
                              fontWeight="medium"
                              fontSize="18px"
                            >
                              Deficiency Logs
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>

                        {/* right side */}
                        {!showInspections && (
                          <SoftBox display="flex" gap={2} pb={1}>
                            <SoftButton
                              variant="contained"
                              color="info"
                              sx={{
                                padding: "7px 12px",
                                fontSize: "16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                              onClick={() =>
                                setDialogue({
                                  type: "AddDeficiency",
                                  isOpen: true,
                                  isEdit: false,
                                  action: "addProjectlog",
                                  inspectionId: "",
                                  projectId: id,
                                })
                              }
                            >
                              Add <FaPlus size={14} />
                            </SoftButton>

                            <SoftButton
                              variant="contained"
                              color="yellow"
                              disabled={logpdfLoading}
                              sx={{
                                padding: "7px 12px",
                                fontSize: "16px",
                                display: "flex",
                                width: "180px",
                                alignItems: "center",
                                gap: 1,
                              }}
                              onClick={() =>
                                downloadDeficiencylogData?.length > 0
                                  ? generateDeficiencyPDF()
                                  : toast.error(
                                      "There is no deficiency log to download."
                                    )
                              }
                            >
                              {logpdfLoading ? (
                                <CircularLoader type="small" />
                              ) : (
                                <>
                                  Download Logs <MdOutlineDownload size={25} />
                                </>
                              )}
                            </SoftButton>
                          </SoftBox>
                        )}
                      </SoftBox>

                      {showInspections &&
                        (rows.length > 0 ? (
                          <SoftBox
                            border="1px solid #02255B"
                            sx={{
                              borderRadius: "9px",
                            }}
                          >
                            <CustomTable
                              columns={columns}
                              rows={rows}
                              radius={true}
                            />
                          </SoftBox>
                        ) : (
                          <SoftBox
                            sx={{
                              borderRadius: "9px",
                              border: "1px solid #02255B",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <SoftBox
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              alignItems="center"
                              width="100%"
                              borderRadius="9px"
                            >
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                                backgroundColor="shadow.main"
                                sx={{
                                  borderTopRightRadius: "9px",
                                  borderTopLeftRadius: "9px",
                                  overflowX: "scroll",
                                  scrollbarWidth: "none",
                                  "&::-webkit-scrollbar": { display: "none" },
                                }}
                              >
                                {columns.map(
                                  ({ name, align, width }, index) => (
                                    <SoftBox
                                      key={index}
                                      width={width}
                                      align={align}
                                      fontWeight="bold"
                                      fontSize="16px"
                                      py="14px"
                                      px="24px"
                                    >
                                      {name}
                                    </SoftBox>
                                  )
                                )}
                              </SoftBox>
                              <SoftTypography
                                fontSize="60px"
                                fontWeight="light"
                                height="70px"
                                color="fade"
                              >
                                0
                              </SoftTypography>
                              <SoftTypography
                                fontSize="25px"
                                fontWeight="medium"
                                color="fade"
                                pb={1.5}
                              >
                                INSPECTIONS
                              </SoftTypography>
                              <EmptyFolderIcon
                                style={{
                                  opacity: 0.8,
                                }}
                              />
                            </SoftBox>
                          </SoftBox>
                        ))}

                      {!showInspections &&
                        (deficiencyRows.length > 0 ? (
                          deficiencylogLoading ? (
                            <SoftBox
                              display="flex"
                              height="100%"
                              justifyContent="center"
                              alignItems="center"
                              p={3}
                            >
                              <SoftTypography
                                variant="subtitle1"
                                color="textSecondary"
                              >
                                <CircularLoader type="large" />
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            <SoftBox
                              border="1px solid #02255B"
                              sx={{
                                borderRadius: "9px",
                              }}
                            >
                              <CustomTable
                                columns={Deficiencycolumns}
                                rows={deficiencyRows}
                                radius={true}
                                whitebackground={true}
                              />
                            </SoftBox>
                          )
                        ) : (
                          <SoftBox
                            sx={{
                              border: "1px solid #02255B",
                              borderRadius: "9px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <SoftBox
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              alignItems="center"
                              width="100%"
                              borderRadius="9px"
                            >
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                                backgroundColor="shadow.main"
                                sx={{
                                  borderTopRightRadius: "9px",
                                  borderTopLeftRadius: "9px",
                                  overflowX: "scroll",
                                  scrollbarWidth: "none",
                                  "&::-webkit-scrollbar": { display: "none" },
                                }}
                              >
                                {Deficiencycolumns.map(
                                  ({ name, align, width }, index) => (
                                    <SoftBox
                                      key={index}
                                      width={width}
                                      align={align}
                                      fontWeight="bold"
                                      fontSize="16px"
                                      py="14px"
                                      px="24px"
                                    >
                                      {name}
                                    </SoftBox>
                                  )
                                )}
                              </SoftBox>
                              <SoftTypography
                                fontSize="60px"
                                fontWeight="light"
                                height="70px"
                                color="fade"
                              >
                                0
                              </SoftTypography>
                              <SoftTypography
                                fontSize="25px"
                                fontWeight="medium"
                                color="fade"
                                pb={1.5}
                              >
                                Deficiency logs
                              </SoftTypography>
                              <EmptyFolderIcon
                                style={{
                                  opacity: 0.8,
                                }}
                              />
                            </SoftBox>
                          </SoftBox>
                        ))}
                    </SoftBox>

                    <SoftBox
                      py={1}
                      sx={{
                        backgroundColor: "#ffffff",
                        bottom: 0,
                        zIndex: 2,
                      }}
                    >
                      <SoftPagination
                        Totalpages={
                          showInspections
                            ? pagination?.totalPages
                            : deficiencylogPagination?.totalPages
                        }
                        currentPage={page}
                        changePage={setPage}
                      />
                    </SoftBox>
                  </>
                </SoftBox>
              </SoftBox>
            )}
          </Card>
        </SoftBox>
      </SoftBox>

      {/* deficiency  pdf block */}
      <SoftBox sx={{ display: "none" }}>
        <Container
          ref={deficiencypdfRef}
          py={4}
          maxWidth={false}
          sx={{ width: "100%" }}
        >
          <Table
            sx={{ width: "1035px", tableLayout: "fixed" }}
            aria-label="deficiency log table"
          >
            {downloadDeficiencylogData?.map((row, index) => (
              <TableRow
                key={row?.id}
                sx={{
                  backgroundColor: !row?.corrected_inspection_id
                    ?.inspection_number
                    ? "#F8FE01"
                    : "white",

                  "& td": {
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                }}
              >
                <TableCell
                  sx={{
                    borderRight: "1px solid #000000",
                    borderBottom: "1px solid #000000",
                    borderLeft: "1px solid #000000",
                    px: 1,
                    width: "45px",
                    verticalAlign: "top", // aligns content to the top vertically
                    textAlign: "center", // centers text horizontally
                  }}
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {index + 1}
                  </SoftTypography>
                </TableCell>

                <TableCell
                  sx={{
                    borderBottom: "1px solid #000000",
                    p: 0,
                    width: "90px",
                    verticalAlign: "top", // aligns content to the top vertically
                    textAlign: "center",
                  }}
                  align="center"
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {row?.deficiency_date !== 0
                      ? getFormattedDate(row?.deficiency_date)
                      : ""}
                  </SoftTypography>
                </TableCell>

                <TableCell
                  sx={{
                    borderBottom: "1px solid #000000",
                    borderLeft: "1px solid #000000",
                    p: 0,
                    width: "80px",
                    verticalAlign: "top", // aligns content to the top vertically
                    textAlign: "center",
                  }}
                  align="center"
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {row?.deficiency_inspection_id?.inspection_number}-
                    {row?.deficiency_inspection_id?.inspection_discipline_start}
                  </SoftTypography>
                </TableCell>

                <TableCell
                  sx={{
                    borderRight: "1px solid #000000",
                    borderBottom: "1px solid #000000",
                    borderLeft: "1px solid #000000",
                    width: "650px",
                    px: 1,
                  }}
                  align="left"
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {row?.inspection_type}
                  </SoftTypography>

                  {row?.location && (
                    <SoftTypography fontSize="12px" fontWeight="bold">
                     Location : {row?.location}
                    </SoftTypography>
                  )}

                  {row?.list_of_dependencies?.map((item, idx) => (
                    <SoftTypography key={idx} fontSize="12px">
                      {item}
                    </SoftTypography>
                  ))}
                </TableCell>

                <TableCell
                  sx={{
                    borderBottom: "1px solid #000000",
                    p: 0,
                    widht: "90px",
                    verticalAlign: "top", // aligns content to the top vertically
                    textAlign: "center",
                  }}
                  align="center"
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {row?.corrected_date !== 0
                      ? getFormattedDate(row?.corrected_date)
                      : ""}
                  </SoftTypography>
                </TableCell>

                <TableCell
                  sx={{
                    borderRight: "1px solid #000000",
                    borderBottom: "1px solid #000000",
                    borderLeft: "1px solid #000000",
                    p: 0,
                    width: "80px",
                    verticalAlign: "top", // aligns content to the top vertically
                    textAlign: "center",
                  }}
                  align="center"
                >
                  <SoftTypography fontSize="12px" fontWeight="bold">
                    {row?.corrected_inspection_id?.inspection_number}-
                    {row?.corrected_inspection_id?.inspection_discipline_start}
                  </SoftTypography>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Container>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ProjectOverview;
